import { useEffect, useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShareButton } from "@/components/ShareButton";
import { SpeakButton } from "@/components/SpeakButton";
import { riskSpeechText } from "@/lib/speech";
import { ShieldAlert, Loader2, RefreshCw, Eye, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const riskStyle = (r) => {
  const v = (r || "").toLowerCase();
  if (v === "high") return { bg: "bg-red-600", text: "text-red-700", soft: "bg-red-50 border-red-200" };
  if (v === "medium") return { bg: "bg-amber-500", text: "text-amber-700", soft: "bg-amber-50 border-amber-200" };
  return { bg: "bg-emerald-600", text: "text-emerald-700", soft: "bg-emerald-50 border-emerald-200" };
};

export function PestRiskPanel({ field }) {
  const { user } = useAuth();
  const [risk, setRisk] = useState(field.pest_risk || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setRisk(field.pest_risk || null); }, [field.id, field.pest_risk]);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/fields/${field.id}/pest-risk`, { params: { language: user?.language || "English", refresh } });
      setRisk(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const s = riskStyle(risk?.overall_risk);

  return (
    <div data-testid="pest-risk-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> Pest &amp; disease risk this week</div>
        {risk && !loading && (
          <button data-testid="pest-risk-refresh-btn" onClick={() => load(true)} className="text-xs text-stone-500 hover:text-[#2D6A4F] flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Refresh</button>
        )}
      </div>

      {!risk && !loading && (
        <Button data-testid="pest-risk-check-btn" onClick={() => load(false)} className="w-full h-11 bg-[#78350F] hover:bg-[#5a2609] text-white rounded-xl font-semibold">
          <ShieldAlert className="h-4 w-4 mr-2" /> Check this week's pest risk
        </Button>
      )}
      {loading && <div className="flex items-center gap-2 text-sm text-stone-500 py-2"><Loader2 className="h-4 w-4 animate-spin" /> Reading the forecast for pests…</div>}

      {risk && !loading && (
        <div className="space-y-3">
          <div className={`rounded-xl border p-3 ${s.soft}`}>
            <div className="flex items-center gap-2 mb-1">
              <span data-testid="pest-risk-overall" className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full text-white ${s.bg}`}>{risk.overall_risk} risk</span>
              <span className="text-[11px] text-stone-500">{new Date(risk.generated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {risk.weather_source}</span>
            </div>
            <p className="text-sm text-stone-700 leading-relaxed">{risk.summary}</p>
          </div>

          <ul className="space-y-2">
            {(risk.warnings || []).map((w, i) => {
              const ws = riskStyle(w.risk);
              return (
                <li key={i} data-testid={`pest-warning-${i}`} className="rounded-xl border border-stone-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-emerald-950 text-sm flex items-center gap-1.5 flex-wrap">
                      {w.problem}
                      {w.seen_before && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#78350F] text-white"><Eye className="h-3 w-3" /> seen on your farm</span>}
                    </div>
                    <span className={`text-[10px] font-bold uppercase shrink-0 ${ws.text}`}>{w.risk}</span>
                  </div>
                  {w.crop && <div className="text-[11px] text-stone-500 mt-0.5">{w.crop}</div>}
                  <div className="text-xs text-stone-600 mt-1.5"><b>Why:</b> {w.trigger}</div>
                  <div className="text-xs text-emerald-900 mt-1"><b>Do:</b> {w.action} <span className="text-stone-500">— {w.when}</span></div>
                </li>
              );
            })}
          </ul>

          {risk.safe_windows?.length > 0 && (
            <div className="rounded-xl bg-[#EAF6EF] p-3">
              <div className="text-[11px] uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1 flex items-center gap-1"><CalendarCheck className="h-3.5 w-3.5" /> Good windows</div>
              <ul className="space-y-1">{risk.safe_windows.map((t, i) => <li key={i} className="text-xs text-stone-700 flex gap-1.5"><span className="text-[#52B788]">•</span>{t}</li>)}</ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <SpeakButton text={riskSpeechText(risk, field.name)} language={user?.language} size="sm" label="Listen" />
            <ShareButton kind="pest_risk" refId={field.id} size="sm"
              title={`${risk.overall_risk} pest risk this week near ${field.location || field.name}`}
              summary={`${risk.summary}${risk.warnings?.[0] ? `\nWatch for: ${risk.warnings.slice(0, 3).map((w) => w.problem).join(", ")}` : ""}`} />
          </div>
        </div>
      )}
    </div>
  );
}
